import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Shapes, Plus, Trash2, Save, Play, Code2, Loader2, Eye, LayoutGrid, LayoutDashboard, Globe, UploadCloud, Star, Check, Ban, Sparkles, Paperclip, FileText, X, Image as ImageIcon } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { CustomVisualSandbox } from '@/components/CustomVisualSandbox';
import { loadTenantCustomVisuals } from '@/lib/customVisualLoader';

const STARTER_CODE = `// Author SDK: call inzara.render() with your draw function.
// ctx = { container, data, config, width, height, theme, formatNumber }
inzara.render(function (ctx) {
  var total = ctx.data.reduce(function (s, r) {
    var v = Object.values(r).find(function (x) { return typeof x === 'number'; });
    return s + (v || 0);
  }, 0);
  var el = document.createElement('div');
  el.style.cssText = 'height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;';
  el.innerHTML =
    '<div style="font-size:44px;font-weight:800;color:' + (ctx.config.color || '#6366f1') + '">' +
      ctx.formatNumber(total) + '</div>' +
    '<div style="font-size:12px;color:#888;text-transform:uppercase;letter-spacing:1px">' +
      (ctx.config.label || 'Total') + '</div>';
  ctx.container.appendChild(el);
});`;

const SAMPLE_DATA = [
  { category: 'North', value: 4200 },
  { category: 'South', value: 3100 },
  { category: 'East', value: 5400 },
  { category: 'West', value: 2800 },
];

const EMPTY = {
  name: '', description: '', icon: 'Shapes', js_code: STARTER_CODE,
  config_schema: { fields: [] }, enabled: true,
};

export default function CustomVisualsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list view
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [previewCode, setPreviewCode] = useState(STARTER_CODE);
  const [previewCfg, setPreviewCfg] = useState({});
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [communityTemplates, setCommunityTemplates] = useState([]);
  const [publishing, setPublishing] = useState(null);
  const [useLoading, setUseLoading] = useState(null); // key/id currently being added to a dashboard
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiFiles, setAiFiles] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [improvePrompt, setImprovePrompt] = useState('');
  const [improveLoading, setImproveLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.listCustomVisuals(); setItems(res.items || []); }
    catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => { setForm(EMPTY); setEditing('new'); setPreviewCode(EMPTY.js_code); setPreviewCfg({}); };

  const addAiFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    setAiFiles(prev => [...prev, ...picked].slice(0, 6));
    e.target.value = '';
  };
  const removeAiFile = (i) => setAiFiles(prev => prev.filter((_, idx) => idx !== i));

  const improveWithAi = async () => {
    if (!improvePrompt.trim()) { toast.error('Describe the change you want'); return; }
    if (!form.js_code?.trim()) { toast.error('No code to improve yet'); return; }
    setImproveLoading(true);
    try {
      const fd = new FormData();
      fd.append('prompt', improvePrompt.trim());
      fd.append('current_code', form.js_code);
      const res = await api.aiGenerateCustomVisual(fd);
      const cfg = { fields: (res.config_schema?.fields) || form.config_schema?.fields || [] };
      setForm(f => ({ ...f, js_code: res.js_code, config_schema: cfg }));
      setPreviewCode(res.js_code);
      setImprovePrompt('');
      toast.success('Updated — check the preview');
    } catch (e) { toast.error(e.message || 'AI edit failed'); }
    finally { setImproveLoading(false); }
  };

  const generateWithAi = async () => {
    if (!aiPrompt.trim()) { toast.error('Describe the visual you want'); return; }
    setAiLoading(true);
    try {
      const fd = new FormData();
      fd.append('prompt', aiPrompt.trim());
      aiFiles.forEach(f => fd.append('files', f));
      const res = await api.aiGenerateCustomVisual(fd);
      const cfg = { fields: (res.config_schema?.fields) || [] };
      setForm({ name: res.name || 'AI Visual', description: res.description || '', icon: res.icon || 'Shapes', js_code: res.js_code, config_schema: cfg, enabled: true });
      setEditing('new');
      setPreviewCode(res.js_code);
      setPreviewCfg(Object.fromEntries((cfg.fields || []).map(f => [f.key, f.default])));
      setAiOpen(false);
      setAiPrompt(''); setAiFiles([]);
      toast.success('AI drafted your visual — preview & save');
    } catch (e) { toast.error(e.message || 'AI generation failed'); }
    finally { setAiLoading(false); }
  };

  const openGallery = async () => {
    setGalleryOpen(true);
    if (templates.length === 0) {
      try { const res = await api.getVisualGallery(); setTemplates(res.templates || []); }
      catch (e) { toast.error(e.message); }
    }
    loadCommunity();
  };

  const loadCommunity = async () => {
    try { const res = await api.getCommunityGallery('visual'); setCommunityTemplates(res.templates || []); }
    catch (e) { /* community is optional */ }
  };

  const publishVisual = async (v) => {
    setPublishing(v.viz_key);
    try {
      const res = await api.publishToCommunity('visual', v.visual_id);
      toast.success(res.status === 'pending'
        ? `"${v.name}" submitted — awaiting super-admin approval`
        : `"${v.name}" published to the Community Gallery`);
      loadCommunity();
    } catch (e) { toast.error(e.message); }
    finally { setPublishing(null); }
  };

  const unpublishCommunity = async (t) => {
    if (!window.confirm(`Remove "${t.name}" from the Community Gallery?`)) return;
    try { await api.unpublishCommunity(t.gallery_id); toast.success('Unpublished'); loadCommunity(); }
    catch (e) { toast.error(e.message); }
  };

  const moderate = async (t, patch) => {
    try { await api.moderateCommunity(t.gallery_id, patch); loadCommunity(); }
    catch (e) { toast.error(e.message); }
  };

  const applyVisualTemplate = (t) => {
    if (t.gallery_id) api.trackCommunityImport(t.gallery_id).catch(() => {});
    const cfg = { fields: (t.config_schema?.fields) || [] };
    setForm({ name: t.name, description: t.description, icon: t.icon || 'Shapes', js_code: t.js_code, config_schema: cfg, enabled: true });
    setEditing('new');
    setPreviewCode(t.js_code);
    setPreviewCfg(Object.fromEntries((cfg.fields || []).map(f => [f.key, f.default])));
    setGalleryOpen(false);
  };
  const startEdit = (v) => {
    setForm({ ...v, config_schema: v.config_schema || { fields: [] } });
    setEditing(v.visual_id); setPreviewCode(v.js_code || ''); setPreviewCfg({});
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Give your visual a name');
    setSaving(true);
    try {
      if (editing === 'new') { await api.createCustomVisual(form); toast.success('Custom visual created'); }
      else { await api.updateCustomVisual(editing, form); toast.success('Saved'); }
      await load();
      await loadTenantCustomVisuals(); // refresh registry so the new viz is selectable
      setEditing(null);
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete "${v.name}"?`)) return;
    try { await api.deleteCustomVisual(v.visual_id); toast.success('Deleted'); await load(); await loadTenantCustomVisuals(); }
    catch (e) { toast.error(e.message); }
  };

  // Create a brand-new dashboard whose first tile uses the given custom visual,
  // then jump straight into the dashboard builder (2-click chart creation).
  const createDashboardWithVisual = async (vizKey, name, description) => {
    const report = await api.createReport({ name: `${name} Dashboard`, description: description || '', visualization_type: 'dashboard' });
    const tileId = `v_cv_${Date.now()}`;
    const visual = { id: tileId, vizType: vizKey, title: name, rowDims: [], colDim: '', valueMeasures: [], filterDims: [] };
    await api.updateReport(report.report_id, {
      name: `${name} Dashboard`, description: description || '',
      query_definition: { model_id: '', dimensions: [], measures: [] },
      config: { visuals: [visual], visualConfigs: {}, gridLayouts: { [tileId]: { x: 0, y: 0, w: 6, h: 8 } }, showGrid: true, showLegend: true, designerMode: 'dashboard' },
    });
    navigate(`/dashboards/builder?id=${report.report_id}`);
  };

  // From a saved custom visual card → new dashboard tile.
  const addSavedToDashboard = async (v) => {
    setUseLoading(v.viz_key);
    try {
      await loadTenantCustomVisuals();
      await createDashboardWithVisual(v.viz_key, v.name, v.description);
    } catch (e) { toast.error(e.message); }
    finally { setUseLoading(null); }
  };

  // From a gallery template → save it as a custom visual, then new dashboard tile.
  const addTemplateToDashboard = async (t) => {
    setUseLoading(t.key);
    try {
      if (t.gallery_id) api.trackCommunityImport(t.gallery_id).catch(() => {});
      const cfg = { fields: (t.config_schema?.fields) || [] };
      const created = await api.createCustomVisual({ name: t.name, description: t.description, icon: t.icon || 'Shapes', js_code: t.js_code, config_schema: cfg, enabled: true });
      await loadTenantCustomVisuals();
      setGalleryOpen(false);
      toast.success(`"${t.name}" added — opening dashboard…`);
      await createDashboardWithVisual(created.viz_key, created.name, created.description);
    } catch (e) { toast.error(e.message); }
    finally { setUseLoading(null); }
  };

  // config schema field helpers
  const fields = form.config_schema?.fields || [];
  const setFields = (next) => setForm(f => ({ ...f, config_schema: { ...(f.config_schema || {}), fields: next } }));
  const addField = () => setFields([...fields, { key: '', label: '', type: 'string', default: '' }]);
  const updField = (i, patch) => setFields(fields.map((f, idx) => idx === i ? { ...f, ...patch } : f));
  const delField = (i) => setFields(fields.filter((_, idx) => idx !== i));

  if (editing) {
    return (
      <div className="p-6 max-w-6xl mx-auto" data-testid="custom-visual-editor-page">
        <div className="flex items-center justify-between mb-5">
          <div>
            <button onClick={() => setEditing(null)} className="text-sm text-primary hover:underline mb-1" data-testid="cv-back-btn">← Back to visuals</button>
            <h1 className="text-2xl font-bold">{editing === 'new' ? 'New Custom Visual' : 'Edit Custom Visual'}</h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setPreviewCode(form.js_code); setPreviewCfg(Object.fromEntries(fields.map(f => [f.key, f.default]))); }} data-testid="cv-run-preview-btn"><Play className="w-4 h-4 mr-1" />Run preview</Button>
            <Button onClick={save} disabled={saving} data-testid="cv-save-btn">{saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Save</Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="space-y-4">
            <Card className="p-4 space-y-3">
              <div>
                <Label>Name</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Big KPI Number" data-testid="cv-name-input" />
              </div>
              <div>
                <Label>Description</Label>
                <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="What does it show?" data-testid="cv-desc-input" />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.enabled} onCheckedChange={v => setForm({ ...form, enabled: v })} data-testid="cv-enabled-switch" />
                <Label className="!mb-0">Enabled (selectable in the visual picker)</Label>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center gap-2 mb-2 text-sm font-semibold"><Code2 className="w-4 h-4" /> Visualization code</div>
              <Textarea value={form.js_code} onChange={e => setForm({ ...form, js_code: e.target.value })}
                className="font-mono text-xs h-72" spellCheck={false} data-testid="cv-code-editor" />
              <p className="text-[11px] text-muted-foreground mt-2">Runs sandboxed in an isolated iframe. Use <code>inzara.render(fn)</code>. No network/parent access.</p>
              <div className="mt-3 pt-3 border-t border-border/40">
                <div className="flex items-center gap-2 text-xs font-medium mb-2 text-primary"><Sparkles className="w-3.5 h-3.5" /> Improve with AI</div>
                <div className="flex gap-2">
                  <Textarea value={improvePrompt} onChange={e => setImprovePrompt(e.target.value)} placeholder="e.g. make the bars horizontal, add a target line at 100, use a teal palette…" className="text-xs h-16 flex-1" data-testid="cv-improve-prompt" />
                  <Button onClick={improveWithAi} disabled={improveLoading} className="self-stretch" data-testid="cv-improve-btn">
                    {improveLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold">Config options</div>
                <Button size="sm" variant="outline" onClick={addField} data-testid="cv-add-field-btn"><Plus className="w-3 h-3 mr-1" />Add option</Button>
              </div>
              {fields.length === 0 && <p className="text-xs text-muted-foreground">No options. Add fields users can tweak (exposed as <code>ctx.config</code>).</p>}
              <div className="space-y-2">
                {fields.map((f, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`cv-field-row-${i}`}>
                    <Input className="col-span-3" placeholder="key" value={f.key} onChange={e => updField(i, { key: e.target.value })} />
                    <Input className="col-span-3" placeholder="label" value={f.label} onChange={e => updField(i, { label: e.target.value })} />
                    <select className="col-span-2 h-9 rounded-md border bg-background text-sm px-1" value={f.type} onChange={e => updField(i, { type: e.target.value })}>
                      <option value="string">string</option><option value="number">number</option><option value="color">color</option><option value="boolean">boolean</option>
                    </select>
                    <Input className="col-span-3" placeholder="default" value={f.default ?? ''} onChange={e => updField(i, { default: e.target.value })} />
                    <button className="col-span-1 text-red-500" onClick={() => delField(i)}><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          <div>
            <Card className="p-4 sticky top-4">
              <div className="flex items-center gap-2 mb-3 text-sm font-semibold"><Eye className="w-4 h-4" /> Live preview <span className="text-[10px] text-muted-foreground font-normal">(sample data)</span></div>
              <div className="border rounded-lg overflow-hidden bg-card">
                <CustomVisualSandbox jsCode={previewCode} data={SAMPLE_DATA} config={previewCfg} height={340} />
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">Click "Run preview" after editing to re-execute with your latest code.</p>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="custom-visuals-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Shapes className="w-6 h-6 text-primary" /> Custom Visuals SDK</h1>
          <p className="text-sm text-muted-foreground mt-1">Author sandboxed JavaScript visualizations and use them across dashboards & reports.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openGallery} data-testid="cv-gallery-btn"><LayoutGrid className="w-4 h-4 mr-1" />Browse Gallery</Button>
          <Button variant="outline" onClick={() => setAiOpen(true)} data-testid="cv-ai-btn" className="border-primary/40 text-primary hover:bg-primary/10"><Sparkles className="w-4 h-4 mr-1" />Generate with AI</Button>
          <Button onClick={startNew} data-testid="cv-new-btn"><Plus className="w-4 h-4 mr-1" />New Visual</Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="cv-empty">
          <Shapes className="w-10 h-10 mx-auto mb-3 opacity-40" />
          No custom visuals yet. Create one to extend INZARA's chart library.
          <div className="mt-4"><Button variant="outline" onClick={openGallery} data-testid="cv-empty-gallery-btn"><LayoutGrid className="w-4 h-4 mr-1" />Browse the Gallery</Button></div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(v => (
            <Card key={v.visual_id} className="p-4 hover:shadow-md transition-shadow" data-testid={`cv-card-${v.viz_key}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2"><Shapes className="w-5 h-5 text-primary" /><span className="font-semibold">{v.name}</span></div>
                <Badge variant={v.enabled ? 'default' : 'secondary'}>{v.enabled ? 'Enabled' : 'Disabled'}</Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-2 line-clamp-2 min-h-[2rem]">{v.description || 'No description'}</p>
              <div className="text-[10px] text-muted-foreground mt-1 font-mono">{v.viz_key}</div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => startEdit(v)} data-testid={`cv-edit-${v.viz_key}`}>Edit</Button>
                <Button size="sm" variant="ghost" title="Publish to Community Gallery" disabled={publishing === v.viz_key} onClick={() => publishVisual(v)} data-testid={`cv-publish-${v.viz_key}`}>
                  {publishing === v.viz_key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
                </Button>
                <Button size="sm" variant="ghost" className="text-red-500" onClick={() => remove(v)} data-testid={`cv-delete-${v.viz_key}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
              <Button size="sm" variant="secondary" className="w-full mt-2" disabled={useLoading === v.viz_key} onClick={() => addSavedToDashboard(v)} data-testid={`cv-use-${v.viz_key}`}>
                {useLoading === v.viz_key ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <LayoutDashboard className="w-3.5 h-3.5 mr-1" />}Use this visual
              </Button>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="cv-gallery-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><LayoutGrid className="w-5 h-5 text-primary" /> Visual Gallery</DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-sm text-muted-foreground -mt-1">Pick a ready-made visual — it loads into the editor with a live preview so you can tweak and save.</DialogDescription>
          {templates.length === 0 ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              {templates.map(t => {
                const Icon = LucideIcons[t.icon] || Shapes;
                return (
                  <div key={t.key} data-testid={`cv-template-${t.key}`}
                    className="text-left border rounded-lg p-3 hover:border-primary hover:shadow-md transition-all bg-card flex flex-col">
                    <button onClick={() => applyVisualTemplate(t)} className="text-left" data-testid={`cv-template-customize-${t.key}`}>
                      <div className="flex items-center gap-2">
                        <Icon className="w-5 h-5 text-primary shrink-0" />
                        <span className="font-semibold">{t.name}</span>
                        <Badge variant="secondary" className="ml-auto text-[10px]">{t.category}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{t.description}</p>
                    </button>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" variant="outline" className="flex-1" onClick={() => applyVisualTemplate(t)} data-testid={`cv-template-edit-${t.key}`}>Customize</Button>
                      <Button size="sm" className="flex-1" disabled={useLoading === t.key} onClick={() => addTemplateToDashboard(t)} data-testid={`cv-template-use-${t.key}`}>
                        {useLoading === t.key ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <LayoutDashboard className="w-3.5 h-3.5 mr-1" />}Use this visual
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Community-published visuals (shared across tenants) */}
          <div className="mt-6 pt-4 border-t border-border/50">
            <div className="flex items-center gap-2 mb-1">
              <Globe className="w-4 h-4 text-primary" />
              <h3 className="font-semibold text-sm">Community Gallery</h3>
              <Badge variant="secondary" className="text-[10px]">{communityTemplates.length}</Badge>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Custom visuals published by admins across all tenants. Publish your own with the <Globe className="w-3 h-3 inline" /> button on a saved visual.</p>
            {communityTemplates.length === 0 ? (
              <p className="text-xs text-muted-foreground italic py-2" data-testid="cv-community-empty">Nothing published yet — be the first to share a visual!</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {communityTemplates.map(t => {
                  const Icon = LucideIcons[t.icon] || Shapes;
                  return (
                    <div key={t.gallery_id} data-testid={`cv-community-${t.gallery_id}`}
                      className={`text-left border rounded-lg p-3 hover:border-primary hover:shadow-md transition-all bg-card flex flex-col ${t.featured ? 'border-amber-400/60 ring-1 ring-amber-400/30' : ''}`}>
                      <div className="flex items-center gap-2">
                        <Icon className="w-5 h-5 text-primary shrink-0" />
                        <span className="font-semibold">{t.name}</span>
                        {t.featured && <Badge className="text-[10px] gap-1 bg-amber-500/15 text-amber-500 border-amber-500/30" data-testid={`cv-community-featured-${t.gallery_id}`}><Star className="w-2.5 h-2.5 fill-amber-500" />Featured</Badge>}
                        {t.status && t.status !== 'approved' && <Badge variant="outline" className={`text-[10px] ${t.status === 'rejected' ? 'text-red-500 border-red-500/30' : 'text-amber-500 border-amber-500/30'}`}>{t.status}</Badge>}
                        <Badge variant="outline" className="ml-auto text-[10px] gap-1"><Globe className="w-2.5 h-2.5" />Community</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{t.description || 'No description'}</p>
                      <div className="text-[10px] text-muted-foreground/70 mt-1">by {t.published_by_email || 'unknown'} · {t.downloads || 0} imports</div>
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" variant="outline" className="flex-1" onClick={() => applyVisualTemplate(t)} data-testid={`cv-community-customize-${t.gallery_id}`}>Customize</Button>
                        <Button size="sm" className="flex-1" disabled={useLoading === t.key} onClick={() => addTemplateToDashboard(t)} data-testid={`cv-community-use-${t.gallery_id}`}>
                          {useLoading === t.key ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <LayoutDashboard className="w-3.5 h-3.5 mr-1" />}Use
                        </Button>
                        {t.is_owner && (
                          <Button size="sm" variant="ghost" className="text-red-500" title="Unpublish" onClick={() => unpublishCommunity(t)} data-testid={`cv-community-unpublish-${t.gallery_id}`}><Trash2 className="w-4 h-4" /></Button>
                        )}
                      </div>
                      {t.can_moderate && (
                        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-border/40">
                          <span className="text-[10px] text-muted-foreground">Curate:</span>
                          <Button size="sm" variant="ghost" className={`h-7 text-[11px] ${t.featured ? 'text-amber-500' : 'text-muted-foreground'}`} onClick={() => moderate(t, { featured: !t.featured })} data-testid={`cv-community-feature-${t.gallery_id}`}>
                            <Star className={`w-3.5 h-3.5 mr-1 ${t.featured ? 'fill-amber-500' : ''}`} />{t.featured ? 'Unfeature' : 'Feature'}
                          </Button>
                          {t.status !== 'approved'
                            ? <Button size="sm" variant="ghost" className="h-7 text-[11px] text-emerald-500" onClick={() => moderate(t, { status: 'approved' })} data-testid={`cv-community-approve-${t.gallery_id}`}><Check className="w-3.5 h-3.5 mr-1" />Approve</Button>
                            : <Button size="sm" variant="ghost" className="h-7 text-[11px] text-red-500" onClick={() => moderate(t, { status: 'rejected' })} data-testid={`cv-community-reject-${t.gallery_id}`}><Ban className="w-3.5 h-3.5 mr-1" />Reject</Button>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* AI generator */}
      <Dialog open={aiOpen} onOpenChange={(v) => { setAiOpen(v); if (!v) { setAiPrompt(''); setAiFiles([]); } }}>
        <DialogContent className="max-w-xl" data-testid="cv-ai-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> Generate a visual with AI</DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-sm text-muted-foreground -mt-1">Describe the visualization you want. Optionally attach reference graphics (for style) and data files (CSV / PDF / TXT) for the AI to use. It writes the sandboxed code into the editor.</DialogDescription>

          <div className="space-y-4 mt-2">
            <Textarea
              value={aiPrompt}
              onChange={e => setAiPrompt(e.target.value)}
              placeholder="e.g. A radial progress ring showing the first numeric value against a configurable target, with a percentage label in the center and a green-to-amber color based on completion."
              className="h-28"
              data-testid="cv-ai-prompt"
            />

            <div>
              <label className="flex items-center gap-2 border border-dashed border-border rounded-lg px-3 py-3 cursor-pointer hover:border-primary hover:bg-muted/30 transition-all text-sm text-muted-foreground" data-testid="cv-ai-file-label">
                <Paperclip className="w-4 h-4" /> Attach images or documents (optional)
                <input type="file" multiple accept="image/*,.csv,.pdf,.txt,.md,.json,application/pdf,text/csv,text/plain" className="hidden" onChange={addAiFiles} data-testid="cv-ai-file-input" />
              </label>
              {aiFiles.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {aiFiles.map((f, i) => (
                    <div key={i} className="flex items-center gap-1.5 bg-muted/60 rounded-md pl-2 pr-1 py-1 text-xs" data-testid={`cv-ai-file-${i}`}>
                      {f.type.startsWith('image/') ? <ImageIcon className="w-3.5 h-3.5 text-primary" /> : <FileText className="w-3.5 h-3.5 text-primary" />}
                      <span className="max-w-[160px] truncate">{f.name}</span>
                      <button onClick={() => removeAiFile(i)} className="text-muted-foreground hover:text-red-500" data-testid={`cv-ai-file-remove-${i}`}><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button onClick={generateWithAi} disabled={aiLoading} className="w-full" data-testid="cv-ai-generate-btn">
              {aiLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating…</> : <><Sparkles className="w-4 h-4 mr-2" />Generate visual</>}
            </Button>
            <p className="text-[11px] text-muted-foreground text-center -mt-1">Powered by Gemini · You can tweak the code and preview before saving.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
